import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"

const AlertQueueContext = createContext(null)

export function AlertQueueProvider({ children }) {
  const [items, setItems] = useState([])
  const [activeId, setActiveId] = useState(null)
  const processingRef = useRef(false)

  const enqueue = useCallback(
    (item) => {
      setItems((prev) => {
        const next = [...prev, item]
        // Higher priority first (descending)
        next.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
        return next
      })
    },
    [items]
  )

  const start = useCallback(() => {
    if (processingRef.current) return
    processingRef.current = true
    setItems((prev) => {
      if (prev.length === 0) {
        processingRef.current = false
        setActiveId(null)
        return prev
      }
      const current = prev[0]
      setActiveId(current?.id)
      try {
        current?.load?.({
          onCompleted: () => complete({ id: current?.id }),
        })
      } catch (e) {
        // Fail-safe: complete and move on
        complete({ id: current?.id })
      }
      return prev
    })
  }, [])

  const complete = useCallback(({ id }) => {
    setItems((prev) => {
      const next = prev.filter((it) => it.id !== id)
      if (next.length === 0) {
        processingRef.current = false
        setActiveId(null)
        return next
      }
      const current = next[0]
      setActiveId(current?.id)
      try {
        current?.load?.({
          onCompleted: () => complete({ id: current?.id }),
        })
      } catch (e) {
        // proceed to next if load throws
        setTimeout(() => complete({ id: current?.id }), 0)
      }
      return next
    })
  }, [])

  const cancel = useCallback(
    ({ id }) => {
      // Remove item without triggering next
      setItems((prev) => prev.filter((it) => it.id !== id))
      if (activeId === id) {
        setActiveId(null)
        processingRef.current = false
      }
    },
    [activeId]
  )

  const isActive = useCallback((id) => activeId === id, [activeId])

  const value = useMemo(
    () => ({ enqueue, start, complete, cancel, isActive, setActiveId }),
    [enqueue, start, complete, cancel, isActive, setActiveId]
  )

  return (
    <AlertQueueContext.Provider value={value}>
      {children}
    </AlertQueueContext.Provider>
  )
}

export function useAlertQueue() {
  const ctx = useContext(AlertQueueContext)
  if (!ctx) {
    throw new Error("useAlertQueue must be used within AlertQueueProvider")
  }
  return ctx
}
